import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bike, Gamepad2, Upload, Users, Image as ImageIcon, Loader2, Plus } from "lucide-react";
import { api, motoPhotoImageUrl } from "@/lib/api";

export default function MotoGamingSection() {
  // Moto
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [author, setAuthor] = useState("");
  const fileRef = useRef(null);

  // Gaming (simplified)
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState({
    author: "",
    game: "",
    when: "",
    description: "",
    contact: "",
  });
  const [creating, setCreating] = useState(false);

  const loadPhotos = async () => {
    try {
      const r = await api.get("/moto/photos");
      setPhotos(r.data || []);
    } catch {}
  };
  const loadSessions = async () => {
    try {
      const r = await api.get("/gaming/sessions");
      setSessions(r.data || []);
    } catch {}
  };

  useEffect(() => {
    loadPhotos();
    loadSessions();
  }, []);

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Choisis une image (JPG, PNG, WebP).");
      return;
    }
    try {
      setUploading(true);
      const form = new FormData();
      form.append("file", file);
      const params = new URLSearchParams();
      if (caption) params.set("caption", caption);
      if (author) params.set("author", author);
      await api.post(`/moto/photos?${params.toString()}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo ajoutée !");
      setCaption("");
      await loadPhotos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload impossible.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const createSession = async (e) => {
    e.preventDefault();
    if (!session.author.trim() || !session.game.trim() || !session.when.trim()) {
      toast.error("Pseudo, titre et créneau sont nécessaires.");
      return;
    }
    try {
      setCreating(true);
      await api.post("/gaming/sessions", session);
      toast.success("Session proposée, les amis vont adorer.");
      setSession({ author: "", game: "", when: "", description: "", contact: "" });
      await loadSessions();
    } catch {
      toast.error("Impossible de créer la session.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <section id="moto-gaming" className="py-16 lg:py-20 border-y border-white/5 bg-[#0A1020]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <span className="em-eyebrow">Passions partagées</span>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
          Moto & jeux vidéo : se changer les idées, ensemble.
        </h2>
        <p className="mt-3 text-slate-300 max-w-2xl">
          Parler d'autre chose fait aussi du bien. Partage une photo de ta bécane ou propose une
          session de jeu, peu importe laquelle — on est là pour la bonne ambiance.
        </p>

        <div className="mt-10 grid lg:grid-cols-12 gap-6">
          {/* MOTO */}
          <div className="lg:col-span-7">
            <div className="em-card p-6 lg:p-7">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-orange-500/15 border border-orange-500/30 grid place-items-center">
                    <Bike className="w-5 h-5 text-orange-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white font-[Outfit]">Moto — garage</h3>
                    <p className="text-sm text-slate-400">Partage ta bécane, raconte une balade.</p>
                  </div>
                </div>
                <button
                  onClick={onPickFile}
                  disabled={uploading}
                  data-testid="moto-upload-button"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-full"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Envoi…" : "Partager une photo"}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onFileChange}
                  data-testid="moto-file-input"
                />
              </div>

              <div className="mt-5 grid md:grid-cols-2 gap-3">
                <input
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Ton pseudo (facultatif)"
                  data-testid="moto-author"
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                  maxLength={40}
                />
                <input
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Légende (facultative)"
                  data-testid="moto-caption"
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                  maxLength={200}
                />
              </div>

              <div className="mt-6">
                {photos.length === 0 ? (
                  <div
                    className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-slate-400"
                    data-testid="moto-empty"
                  >
                    <ImageIcon className="w-6 h-6 mx-auto mb-3 opacity-70" />
                    Aucune photo pour l'instant. Sois le premier à ouvrir le garage !
                  </div>
                ) : (
                  <div
                    className="grid grid-cols-2 md:grid-cols-3 gap-3"
                    data-testid="moto-gallery"
                  >
                    {photos.map((p) => (
                      <figure
                        key={p.id}
                        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-black/30"
                      >
                        <img
                          src={motoPhotoImageUrl(p.id)}
                          alt={p.caption || "Moto communauté"}
                          loading="lazy"
                          className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        {(p.caption || p.author) && (
                          <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                            {p.caption && (
                              <p className="text-sm text-white font-medium truncate">{p.caption}</p>
                            )}
                            {p.author && (
                              <p className="text-xs text-slate-300">par {p.author}</p>
                            )}
                          </figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* GAMING - simplified */}
          <div className="lg:col-span-5">
            <div className="em-card p-6 lg:p-7">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
                  <Gamepad2 className="w-5 h-5 text-emerald-300" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white font-[Outfit]">Jeux vidéo</h3>
                  <p className="text-sm text-slate-400">Un bon moyen de décompresser, à plusieurs.</p>
                </div>
              </div>

              <p className="mt-4 text-sm text-slate-300">
                Peu importe le jeu, propose un créneau et les autres te rejoignent.
              </p>

              <form onSubmit={createSession} className="mt-5 grid gap-3" data-testid="gaming-form">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    value={session.author}
                    onChange={(e) => setSession({ ...session, author: e.target.value })}
                    placeholder="Pseudo"
                    data-testid="gaming-author"
                    className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                    maxLength={40}
                    required
                  />
                  <input
                    value={session.when}
                    onChange={(e) => setSession({ ...session, when: e.target.value })}
                    placeholder="Quand ? (ex. vendredi 21h)"
                    data-testid="gaming-when"
                    className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                    maxLength={80}
                    required
                  />
                </div>
                <input
                  value={session.game}
                  onChange={(e) => setSession({ ...session, game: e.target.value })}
                  placeholder="Titre de la session (ex. soirée détente, chill en vocal…)"
                  data-testid="gaming-game"
                  className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                  maxLength={80}
                  required
                />
                <textarea
                  rows={2}
                  value={session.description}
                  onChange={(e) => setSession({ ...session, description: e.target.value })}
                  placeholder="Décris l'ambiance, le niveau, ce que tu cherches…"
                  data-testid="gaming-description"
                  className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                  maxLength={400}
                />
                <input
                  value={session.contact}
                  onChange={(e) => setSession({ ...session, contact: e.target.value })}
                  placeholder="Discord / contact (facultatif)"
                  data-testid="gaming-contact"
                  className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                  maxLength={150}
                />
                <button
                  type="submit"
                  disabled={creating}
                  data-testid="gaming-submit"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-emerald-950 font-semibold px-5 py-3 rounded-full"
                >
                  <Plus className="w-4 h-4" />
                  {creating ? "Création…" : "Proposer une session"}
                </button>
              </form>
            </div>

            <div className="mt-5 em-card p-6">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Users className="w-4 h-4 text-emerald-300" /> Sessions ouvertes
              </div>
              <div className="mt-3 space-y-2 max-h-72 overflow-auto pr-1" data-testid="gaming-sessions-list">
                {sessions.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Aucune session pour l'instant. Lance la première !</p>
                ) : (
                  sessions.map((s) => (
                    <div key={s.id} className="rounded-xl bg-white/5 border border-white/10 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-white font-semibold truncate">{s.game}</p>
                        <span className="text-xs text-emerald-300 shrink-0">{s.when}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        par <span className="text-slate-200">{s.author}</span>
                      </p>
                      {s.description && (
                        <p className="text-sm text-slate-300 mt-2">{s.description}</p>
                      )}
                      {s.contact && (
                        <p className="text-xs text-slate-400 mt-2">
                          Contact : <span className="text-slate-200">{s.contact}</span>
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
